import { mdiAlertOutline, mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import {
    FileCabinetFtsOperator,
    fileCabinetFtsLabel,
    fileCabinetFtsParse,
    FileCabinetFtsTerm,
    fileCabinetFtsWithout,
} from '../../services/file-cabinet/file-cabinet.fts';
import { FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import * as S from './file-cabinet.styles';
import type { ChipTone } from './file-cabinet.styles';

const TONES: Record<FileCabinetFtsOperator, ChipTone> = { '+': 'primary', '-': 'danger', '': 'neutral', '>': 'neutral', '<': 'neutral', '~': 'neutral' };

/**
 * Under the toolbar while a full-text search is applied (design: `FtsStrip`): each term as a chip that
 * says what it does and can be removed, how many records matched, and what may not search the way it
 * reads. Reads the APPLIED search — the box may hold a draft not yet sent.
 */
export const FileCabinetFtsStrip = observer(({ store }: { store: FileCabinetStore }): React.ReactElement | null => {
    const search = store.searchDef;
    if ((search.mode !== 'fts' && search.mode !== 'transcript') || !store.search.trim()) {
        return null;
    }
    const { terms, warnings } = fileCabinetFtsParse(store.search);
    return (
        // The terms can be a patient's name: blocked from session replay.
        <S.FtsStrip data-sentry-block aria-label="Full-text search terms">
            <S.FtsChips>
                <S.FtsPreviewLabel>Matching</S.FtsPreviewLabel>
                {terms.map((term, index) => (
                    <TermChip key={`${index}:${term.raw}`} term={term} onRemove={(): void => store.setSearch(fileCabinetFtsWithout(store.search, term))} />
                ))}
                <S.FtsCount aria-live="polite">{countText(store, search.scope)}</S.FtsCount>
            </S.FtsChips>
            {warnings.map(warning => (
                <S.FtsWarning key={warning}>
                    <Icon path={mdiAlertOutline} size={0.6} />
                    <span>{warning}</span>
                </S.FtsWarning>
            ))}
        </S.FtsStrip>
    );
});

function TermChip({ term, onRemove }: { term: FileCabinetFtsTerm; onRemove: () => void }): React.ReactElement {
    const { tag, text } = fileCabinetFtsLabel(term);
    const ignored = term.kind === 'word' && term.ignored;
    return (
        <S.FtsChip ignored={ignored}>
            <S.Chip tone={ignored ? 'neutral' : TONES[term.op]}>{tag}</S.Chip>
            <S.Mono>{text}</S.Mono>
            <S.FtsChipRemove variant="unstyled" type="button" aria-label={`Remove ${text}`} onClick={onRemove}>
                <Icon path={mdiClose} size={0.55} />
            </S.FtsChipRemove>
        </S.FtsChip>
    );
}

/** A count only once the page says so (NS-3); the Filters panel can narrow it further, so "results". */
function countText(store: FileCabinetStore, scope: string | undefined): string {
    const where = scope ? ` · ${scope}` : '';
    if (store.loading || (store.total === undefined && !store.error)) {
        return `Searching…${where}`;
    }
    if (store.error || store.total === undefined) {
        return `Results unknown${where}`;
    }
    return `${store.total.toLocaleString()} result${store.total === 1 ? '' : 's'}${where}`;
}
